import axios from "axios";
import API_CONFIG from "../../config/api";
import { deduplicateEntities } from "../../utils/entityUtils";

const expensesUrl = API_CONFIG.apiUrl("/expenses");
const categoriesUrl = API_CONFIG.apiUrl("/expenses/categories");

export async function fetchExpenseCategories(companyId, moduleScope = "commercial") {
  const response = await axios.get(categoriesUrl, {
    params: { company_id: companyId, module_scope: moduleScope },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function saveExpenseCategory(id, payload) {
  if (id) return axios.put(`${categoriesUrl}/${id}`, payload);
  return axios.post(categoriesUrl, payload);
}

export async function deleteExpenseCategory(id, moduleScope = "commercial") {
  return axios.delete(`${categoriesUrl}/${id}`, { params: { module_scope: moduleScope } });
}

export async function fetchExpenseSuppliers(companyId) {
  const response = await axios.get(API_CONFIG.apiUrl("/users/suppliers"), {
    params: { companyId },
  });
  const supplierRows = Array.isArray(response.data) ? response.data : [];
  return deduplicateEntities(supplierRows);
}

export async function fetchExpenses(params) {
  const response = await axios.get(expensesUrl, { params });
  return {
    rows: response.data?.expenses || [],
    total: Number(response.data?.pagination?.totalItems || 0),
  };
}

export async function fetchExpense(id, moduleScope = "commercial") {
  const response = await axios.get(`${expensesUrl}/${id}`, {
    params: { module_scope: moduleScope },
  });
  return response.data;
}

export async function saveExpense(id, payload) {
  if (id) return axios.put(`${expensesUrl}/${id}`, payload);
  return axios.post(expensesUrl, payload);
}

export async function deleteExpense(id, moduleScope = "commercial") {
  return axios.delete(`${expensesUrl}/${id}`, { params: { module_scope: moduleScope } });
}

export async function fetchExpensePayments(id, companyId, moduleScope = "commercial") {
  const response = await axios.get(API_CONFIG.apiUrl(`/expenses/${id}/payments`), {
    params: { company_id: companyId, module_scope: moduleScope },
  });
  return Array.isArray(response.data)
    ? response.data
    : Array.isArray(response.data?.payments)
    ? response.data.payments
    : [];
}

export async function fetchExpensePaymentModes(companyId) {
  const url = API_CONFIG.apiUrl("/payment-modes");
  const [companyRes, allRes] = await Promise.all([
    axios.get(url, { params: { company_id: companyId, limit: 200 } }),
    axios.get(url, { params: { limit: 200 } }),
  ]);
  const companyModes = companyRes.data?.paymentModes || [];
  const genericModes = (allRes.data?.paymentModes || []).filter((mode) => !mode.company_id);
  return [...companyModes, ...genericModes].filter(
    (mode, index, rows) => index === rows.findIndex((row) => row.id === mode.id)
  );
}

export async function fetchExpenseTreasuryAccounts(companyId, warehouseId, moduleScope = "commercial") {
  const response = await axios.get(API_CONFIG.apiUrl("/treasury/accounts"), {
    params: {
      company_id: companyId,
      warehouse_id: warehouseId,
      module_scope: moduleScope,
      include_inactive: false,
    },
  });
  return response.data?.success ? response.data.data || [] : [];
}

export async function processExpensePayment(payload, idempotencyKey) {
  return axios.post(API_CONFIG.apiUrl("/payments/process-expense-payment"), payload, {
    headers: { "X-Idempotency-Key": idempotencyKey },
  });
}

export async function uploadExpenseDocument(file, moduleScope = "commercial", companyId, warehouseId) {
  const formData = new FormData();
  formData.append("document", file);
  formData.append("module_scope", moduleScope);
  const response = await axios.post(API_CONFIG.apiUrl("/expenses/upload-document"), formData, {
    headers: { "X-Company-Id": companyId },
    params: { company_id: companyId, warehouse_id: warehouseId, module_scope: moduleScope },
  });
  return response.data;
}

export async function fetchExpenseDocumentOptions(companyId, warehouseId, moduleScope, signal) {
  const response = await axios.get(`${expensesUrl}/documents/options`, {
    signal, headers: { 'X-Company-Id': companyId },
    params: { company_id: companyId, warehouse_id: warehouseId, module_scope: moduleScope },
  });
  return response.data;
}

export async function fetchExpenseDocument(id, companyId, moduleScope) {
  const response = await axios.get(API_CONFIG.apiUrl(`/expenses/${id}/document`), {
    params: { company_id: companyId, module_scope: moduleScope },
    responseType: "blob",
  });
  return {
    blob: response.data,
    contentType:
      response.headers?.["content-type"] || response.data?.type || "",
  };
}
